import {announce} from '@github-ui/aria-live'
import {CopilotAppIcon} from '@github-ui/copilot-avatar/CopilotAppIcon'
import {sendEvent} from '@github-ui/hydro-analytics'
import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import type {IpmMetricTags} from '@github-ui/ipm-telemetry'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {Icon} from '@primer/octicons-react'
import {CopilotIcon, MarkGithubIcon, XIcon} from '@primer/octicons-react'
import {Heading, IconButton, Label, LinkButton} from '@primer/react'
import {clsx} from 'clsx'
import {Profiler, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {MergeBoxNudge, MergeBoxNudgeIcon} from './merge-box-nudge'
import styles from './MergeBoxNudgeSection.module.css'

export interface MergeBoxNudgeSectionProps {
  nudge: MergeBoxNudge
}

// An icon cannot cross the payload boundary as a component, so dotcom names one instead.
const CTA_ICONS: Record<MergeBoxNudgeIcon, Icon> = {
  'copilot-app': CopilotAppIcon,
  'mark-github': MarkGithubIcon,
  copilot: CopilotIcon,
}

function analyticsEvent(nudge: MergeBoxNudge, action: string) {
  return {
    category: nudge.analyticsCategory,
    action,
    label: `location:${nudge.analyticsLocation};surface:${nudge.analyticsSurface};variant:${nudge.variant};`,
  }
}

/** The merge box's nudge slot: dotcom supplies every value, so this holds no copy, destination, or eligibility logic. */
export function MergeBoxNudgeSection({nudge}: MergeBoxNudgeSectionProps) {
  const [dismissed, setDismissed] = useState(false)
  const dismissedFocusRef = useRef<HTMLSpanElement>(null)
  const dismissingRef = useRef(false)
  const nudgeElementRef = useRef<HTMLDivElement>(null)
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'MergeBoxNudgeSection',
      surface: nudge.analyticsSurface,
      display_location: nudge.analyticsLocation,
      category: nudge.analyticsCategory,
      nudge_id: nudge.nudgeId,
      variant: nudge.variant,
    }),
    [nudge.analyticsCategory, nudge.analyticsLocation, nudge.analyticsSurface, nudge.nudgeId, nudge.variant],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  // The Hydro half of the impression rides on `data-analytics-visible` below, which the
  // global instrumenter fires once the element is actually on screen.
  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  const handleNudgeRemoved = useCallback(() => {
    if (!dismissingRef.current || nudgeElementRef.current === null) return
    nudgeElementRef.current = null

    // Ref cleanup can run before the removed dismiss button releases focus.
    queueMicrotask(() => {
      if (nudgeElementRef.current !== null) return

      const sentinel = dismissedFocusRef.current
      if (!sentinel) return

      const active = document.activeElement
      if (active === null || active === document.body) {
        sentinel.focus()
      }
      announce(nudge.dismissedAnnouncement)
    })
  }, [nudge.dismissedAnnouncement])

  const nudgeRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        nudgeElementRef.current = element
        return handleNudgeRemoved
      }

      handleNudgeRemoved()
    },
    [handleNudgeRemoved],
  )

  const handleDismiss = async () => {
    if (dismissingRef.current) return
    dismissingRef.current = true

    sendEvent('analytics.click', analyticsEvent(nudge, 'dismiss'))
    reportIpmDismiss(telemetryTags)

    try {
      await verifiedFetch(nudge.dismissPath, {method: 'POST'})
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Hide the nudge even if the server did not record the dismissal.
    } finally {
      dismissingRef.current = true
      setDismissed(true)
    }
  }

  const CtaIcon = nudge.ctaIcon ? CTA_ICONS[nudge.ctaIcon] : undefined
  const visibilityClassName = clsx(
    styles.Container,
    nudge.hideOnMobile && styles.HideOnMobile,
    nudge.hideOnTablet && styles.HideOnTablet,
  )

  const section = dismissed ? null : (
    <div
      ref={nudgeRef}
      className={visibilityClassName}
      data-testid={nudge.testId}
      data-analytics-visible={JSON.stringify(analyticsEvent(nudge, 'visible'))}
    >
      <div className={styles.Layout}>
        <div className={styles.Copy}>
          <Heading as="h3" className={styles.Title}>
            {nudge.title}
            {nudge.label ? <Label variant={nudge.labelVariant ?? 'default'}>{nudge.label}</Label> : null}
          </Heading>
          <p className={styles.Description}>{nudge.description}</p>
        </div>
        <div className={styles.Actions}>
          <LinkButton
            href={nudge.ctaUrl}
            leadingVisual={CtaIcon}
            className={styles.Cta}
            data-testid={`${nudge.testId}-cta`}
            onClick={() => {
              sendEvent('analytics.click', analyticsEvent(nudge, nudge.ctaAnalyticsAction))
              reportIpmAction('cta_click', telemetryTags)
            }}
          >
            {nudge.ctaLabel}
          </LinkButton>
          <IconButton
            icon={XIcon}
            size="small"
            variant="invisible"
            aria-label={nudge.dismissLabel}
            data-testid={`${nudge.testId}-dismiss`}
            onClick={handleDismiss}
            className={styles.Dismiss}
          />
        </div>
      </div>
    </div>
  )

  return (
    <>
      <IpmErrorBoundary tags={telemetryTags} boundaryName="MergeBoxNudgeSection" fallback={null}>
        {renderTiming.isEnabled ? (
          <Profiler id="MergeBoxNudgeSection" onRender={renderTiming.onRender}>
            {section}
          </Profiler>
        ) : (
          section
        )}
      </IpmErrorBoundary>
      {dismissed ? (
        <span ref={dismissedFocusRef} tabIndex={-1} className="sr-only" data-testid={`${nudge.testId}-dismissed`} />
      ) : null}
    </>
  )
}
